/**
 * Výpočty pro přehledy (čisté funkce nad daty, bez databáze):
 * stav a stanoviště včelstva k datu, hlášení počtu včelstev, zakrmení,
 * zimování, kalendář sezóny a data pro graf varroázy.
 */
import type { Apiary, Colony, ColonyEvent, ColonyStatus, ISODate } from './model';
import { feedingSugarKg, isMiteFall } from './rules';
import { colonyEvents } from './group';

type Ev = ColonyEvent;

const forColony = colonyEvents;

/** Kdy včelstvo vzniklo / začalo se evidovat. */
export function colonyStart(c: Colony, events: Ev[]): ISODate {
  const first = forColony(events, c.id).reduce<ISODate | null>((m, e) => (m == null || e.date < m ? e.date : m), null);
  const created = c.createdAt?.slice(0, 10);
  return [c.originDate, first, created].filter((d): d is string => !!d).sort()[0] ?? '1970-01-01';
}

/** Stav včelstva ke dni `date` (včetně změn v ten den). Null = včelstvo ještě neexistovalo. */
export function statusAt(c: Colony, events: Ev[], date: ISODate): ColonyStatus | null {
  if (colonyStart(c, events) > date) return null;
  const changes = forColony(events, c.id)
    .filter((e): e is Extract<Ev, { type: 'status_change' }> => e.type === 'status_change')
    .sort((a, b) => a.date.localeCompare(b.date));
  if (changes.length) {
    const last = changes.filter((e) => e.date <= date).at(-1);
    return last ? last.newStatus : 'active';
  }
  // bez zápisu změny: aktuální stav platí od statusDate
  if (c.status !== 'active') return c.statusDate && c.statusDate > date ? 'active' : c.status;
  return 'active';
}

/** Stanoviště, na kterém včelstvo stálo ke dni `date` (podle přesunů). */
export function apiaryAt(c: Colony, events: Ev[], date: ISODate): string {
  const moves = forColony(events, c.id)
    .filter((e): e is Extract<Ev, { type: 'move' }> => e.type === 'move' && e.date > date)
    .sort((a, b) => b.date.localeCompare(a.date));
  let ap = c.apiaryId;
  for (const m of moves) ap = m.fromApiaryId ?? ap;
  return ap;
}

// ---------------------------------------------------------------------------
// Hlášení počtu včelstev (stav k 1. září)
// ---------------------------------------------------------------------------

export interface CountRow {
  apiary: Apiary;
  colonies: Colony[];
}

/** Živá včelstva na jednotlivých stanovištích k datu. Stanoviště bez včelstev se vynechají. */
export function coloniesByApiaryAt(apiaries: Apiary[], colonies: Colony[], events: Ev[], date: ISODate): CountRow[] {
  const rows = new Map<string, Colony[]>();
  for (const c of colonies) {
    if (c.deletedAt || statusAt(c, events, date) !== 'active') continue;
    const ap = apiaryAt(c, events, date);
    rows.set(ap, [...(rows.get(ap) ?? []), c]);
  }
  return apiaries
    .filter((a) => !a.deletedAt && rows.has(a.id))
    .map((a) => ({ apiary: a, colonies: rows.get(a.id)! }));
}

// ---------------------------------------------------------------------------
// Zakrmení na zimu
// ---------------------------------------------------------------------------

export const FEED_TARGET_SETTING = 'feedTargetKg';
export const DEFAULT_FEED_TARGET = 15;

/** Období podzimního krmení: od 1. 7. do konce roku. */
export function winterFeedingPeriod(year: number) {
  return { from: `${year}-07-01`, to: `${year}-12-31` };
}

export interface FeedRow {
  colony: Colony;
  sugarKg: number;
  feedings: number;
  lastDate?: ISODate;
}

export function winterFeeding(colonies: Colony[], events: Ev[], year: number): FeedRow[] {
  const p = winterFeedingPeriod(year);
  return colonies.map((c) => {
    const fs = forColony(events, c.id).filter(
      (e): e is Extract<Ev, { type: 'feeding' }> => e.type === 'feeding' && e.date >= p.from && e.date <= p.to,
    );
    return {
      colony: c,
      sugarKg: Math.round(fs.reduce((a, e) => a + feedingSugarKg(e), 0) * 10) / 10,
      feedings: fs.length,
      lastDate: fs.map((e) => e.date).sort().at(-1),
    };
  });
}

// ---------------------------------------------------------------------------
// Zimování
// ---------------------------------------------------------------------------

/** Zima `year`/`year+1`: do zimy = živá k 1. 11., přezimovala = živá k 1. 4. */
export function winterDates(year: number) {
  return { into: `${year}-11-01`, out: `${year + 1}-04-01`, autumnFrom: `${year}-08-01`, autumnTo: `${year}-10-31` };
}

export interface WinterColony {
  colony: Colony;
  apiaryId: string;
  /** Stav k 1. 4.; null = jaro ještě nenastalo. */
  outcome: ColonyStatus | null;
  /** Celkový spad roztočů na podzim (srpen–říjen); null = bez kontroly. */
  autumnMites: number | null;
}

export interface WinterRow {
  year: number;
  label: string;
  /** Jaro ještě nenastalo (zima probíhá). */
  ongoing: boolean;
  colonies: WinterColony[];
}

export function wintering(colonies: Colony[], events: Ev[], year: number, today: ISODate): WinterRow {
  const d = winterDates(year);
  const ongoing = today < d.out;
  const list: WinterColony[] = [];
  for (const c of colonies) {
    if (c.deletedAt || statusAt(c, events, d.into) !== 'active') continue;
    const checks = forColony(events, c.id).filter(
      (e): e is Extract<Ev, { type: 'varroa_check' }> =>
        e.type === 'varroa_check' && e.date >= d.autumnFrom && e.date <= d.autumnTo && isMiteFall(e),
    );
    list.push({
      colony: c,
      apiaryId: apiaryAt(c, events, d.into),
      outcome: ongoing ? statusAt(c, events, today) : statusAt(c, events, d.out),
      autumnMites: checks.length ? checks.reduce((a, e) => a + (e.miteCount ?? 0), 0) : null,
    });
  }
  return { year, label: `${year}/${String(year + 1).slice(2)}`, ongoing, colonies: list };
}

/** Zimy, pro které jsou data (od nejnovější). */
export function winterYears(colonies: Colony[], events: Ev[], today: ISODate): number[] {
  // jen zimy, kdy už se v aplikaci něco zapisovalo (ne podle data vzniku včelstva)
  const ids = new Set(colonies.filter((c) => !c.deletedAt).map((c) => c.id));
  const dates = events
    .filter((e) => !e.deletedAt)
    .filter((e) => e.colonyIds.some((id) => ids.has(id)))
    .map((e) => e.date)
    .sort();
  if (!dates.length) return [];
  const f = dates[0];
  // první zima: ta, do které spadá první zápis (zápis v lednu–březnu patří k předchozí zimě)
  const first = Number(f.slice(0, 4)) - (f.slice(5) < '04-01' ? 1 : 0);
  const ty = Number(today.slice(0, 4));
  const last = today >= `${ty}-11-01` ? ty : ty - 1;
  const ys: number[] = [];
  for (let y = last; y >= first; y--) ys.push(y);
  return ys;
}

// ---------------------------------------------------------------------------
// Kalendář sezóny
// ---------------------------------------------------------------------------

export interface CalendarRow {
  colony: Colony;
  /** Úseky s nasazenými medníky: od–do a počet. */
  supers: { from: ISODate; to: ISODate; count: number }[];
  treatments: ISODate[];
  feedings: ISODate[];
  harvests: ISODate[];
  inspections: ISODate[];
}

export function seasonCalendar(colonies: Colony[], events: Ev[], from: ISODate, to: ISODate): CalendarRow[] {
  return colonies.map((c) => {
    const evs = [...forColony(events, c.id)].sort((a, b) => a.date.localeCompare(b.date));
    const dates = (t: Ev['type']) => evs.filter((e) => e.type === t && e.date >= from && e.date <= to).map((e) => e.date);
    // počet medníků na začátku období ze všech dřívějších změn
    let count = 0;
    for (const e of evs) if (e.type === 'super_change' && e.date < from) count = Math.max(0, count + (e.action === 'add' ? e.count : -e.count));
    const segs: CalendarRow['supers'] = [];
    let segFrom: ISODate = from;
    for (const e of evs) {
      if (e.type !== 'super_change' || e.date < from || e.date > to) continue;
      const next = Math.max(0, count + (e.action === 'add' ? e.count : -e.count));
      if (next === count) continue;
      if (count > 0 && e.date > segFrom) segs.push({ from: segFrom, to: e.date, count });
      count = next;
      segFrom = e.date;
    }
    if (count > 0) segs.push({ from: segFrom, to, count });
    return {
      colony: c,
      supers: segs,
      treatments: dates('treatment'),
      feedings: dates('feeding'),
      harvests: dates('harvest'),
      inspections: dates('inspection'),
    };
  });
}

// ---------------------------------------------------------------------------
// Graf varroázy
// ---------------------------------------------------------------------------

export interface VarroaSeries {
  /** Spady (podložka, spad po ošetření): datum, počet roztočů, dní na podložce. */
  falls: { date: ISODate; mites: number; days?: number; afterTreatment: boolean }[];
  treatments: { date: ISODate; product: string }[];
}

export function varroaSeries(colonyId: string, events: Ev[], from: ISODate, to: ISODate): VarroaSeries {
  const evs = forColony(events, colonyId).filter((e) => e.date >= from && e.date <= to);
  return {
    falls: evs
      .filter((e): e is Extract<Ev, { type: 'varroa_check' }> => e.type === 'varroa_check' && isMiteFall(e) && Number.isFinite(e.miteCount))
      .map((e) => ({ date: e.date, mites: e.miteCount, days: e.days, afterTreatment: e.method === 'post_treatment' }))
      .sort((a, b) => a.date.localeCompare(b.date)),
    treatments: evs
      .filter((e): e is Extract<Ev, { type: 'treatment' }> => e.type === 'treatment')
      .map((e) => ({ date: e.date, product: e.productName }))
      .sort((a, b) => a.date.localeCompare(b.date)),
  };
}

/** Den v roce jako číslo (pro osu x). */
export function dayIndex(date: ISODate, from: ISODate): number {
  return Math.round((Date.parse(date) - Date.parse(from)) / 86400000);
}
